import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/AlertDialog";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import RHFTextArea from "@/components/form/RHFTextArea";
import { RotateCcw, X } from "lucide-react";

import { getStatusLabel } from "../constants/actions";
import { UNDO_REASON_MAX_LENGTH, undoStatusReasonDefaultValues, undoStatusReasonSchema } from "../validation.schema";

/**
 * The recruiter's own confirmation before a status undo.
 *
 * Same shape as the job-fit apply attestation next door: the kit's
 * AlertDialog for a choice that has to be made explicitly, the kit's Checkbox
 * for the attestation, and theme tokens throughout — an undo modal is not a
 * place to hand-roll an overlay or hardcode a hex.
 *
 * The reason is mandatory and goes through the kit's RHFTextArea on the same
 * zod-resolver pattern as DeleteUserModal, so the asterisk, the error text and
 * the character counter all come from the wrapper rather than from here.
 *
 * `previousStatus` is the stage the server says an undo lands on, so the
 * question names a real stage rather than the words "Previous Stage".
 */
const StatusUndoConfirmModal = ({ isOpen, onClose, onConfirm, candidateName, currentStatus, previousStatus, isLoading, confirmationText }) => {
  const [confirmed, setConfirmed] = useState(false);

  const methods = useForm({
    resolver: zodResolver(undoStatusReasonSchema),
    mode: "onChange",
    defaultValues: undoStatusReasonDefaultValues,
  });
  const { handleSubmit } = methods;

  const currentLabel = getStatusLabel(currentStatus, "their current stage");
  const previousLabel = getStatusLabel(previousStatus, "their previous stage");

  const dismiss = () => {
    if (isLoading) return;
    onClose();
  };

  // `reason` arrives normalized: the schema transforms before it validates, so
  // what the server stores is what the form measured. The confirmation text is
  // the page's own state, so only the reason travels back up.
  const submit = ({ reason }) => onConfirm(reason);

  return (
    <AlertDialog open={!!isOpen} onOpenChange={(open) => !open && dismiss()}>
      <AlertDialogContent className="!max-w-2xl" overlayClassName="supports-backdrop-filter:backdrop-blur-md" onOverlayClick={dismiss}>
        <ButtonShadcn
          variant="ghost"
          size="icon"
          onClick={dismiss}
          disabled={isLoading}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-background-tertiary text-text-secondary transition-colors hover:bg-background-hover hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={16} aria-hidden="true" />
        </ButtonShadcn>

        <AlertDialogHeader>
          <AlertDialogTitle className="border-b border-border-primary pb-1 pr-8 text-lg">Undo this status change?</AlertDialogTitle>
          <AlertDialogDescription className="pr-8">
            This moves <span className="font-semibold text-text-primary">{candidateName || "this candidate"}</span> from{" "}
            <span className="font-semibold text-text-primary">{currentLabel}</span> back to <span className="font-semibold text-text-primary">{previousLabel}</span>. It
            is recorded against the application.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submit)}>
            <RHFTextArea
              name="reason"
              id="undo-reason"
              label="Reason"
              placeholder="Why is this status being rolled back?"
              required
              rows={4}
              maxLength={UNDO_REASON_MAX_LENGTH}
              counter="near-limit"
              disabled={isLoading}
              className="mb-0.5"
              inputClass="resize-y min-h-24 max-h-48 overflow-y-auto"
            />

            <label className="flex items-start gap-2 rounded-lg border border-border-primary bg-background-secondary px-3 py-2.5 text-sm text-text-primary mb-4">
              <Checkbox checked={confirmed} disabled={isLoading} onCheckedChange={(value) => setConfirmed(!!value)} className="mt-0.5" />
              <span>{confirmationText || "Loading…"}</span>
            </label>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={onClose} disabled={isLoading}>
                Cancel
              </AlertDialogCancel>
              <Button
                type="submit"
                disabled={isLoading || !confirmed || !confirmationText}
                className="inline-flex h-8 items-center justify-center gap-2 rounded-md bg-button-secondary px-4 text-sm font-medium text-text-base disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading && <RotateCcw size={14} className="animate-spin" aria-hidden="true" />}
                {isLoading ? "Undoing…" : "Confirm Undo"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default StatusUndoConfirmModal;
